import { useRef, useState } from 'react';
import { BRUSH_SIZES, PALETTE, TOOLS } from '@shared/protocol.js';
import { SHAPE_GROUPS, SHAPE_LABELS, shapePathData, isOpenShape } from '@shared/shapes.js';
import type { ToolState } from '@/canvas/engine';
import { useDismiss } from '../kit';
import {
  IconPen, IconMarker, IconHighlighter, IconEraser, IconScissors, IconShapes,
  IconText, IconStamp, IconLaser, IconHand, IconNeon, IconSelect, IconFill,
} from '../icons';

/**
 * Preview of a shape, drawn from the same geometry the canvas uses — so the
 * picker can never drift out of step with what actually gets drawn.
 */
function ShapePreview({ shape, size = 26 }: { shape: string; size?: number }) {
  const pad = 2.5;
  const box = size - pad * 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <path
        d={shapePathData(shape, pad, pad, box, box)}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {shape === 'arrow' || shape === 'double-arrow' ? (
        <path
          d={shape === 'double-arrow'
            ? `M${pad} ${pad}l4 0M${pad} ${pad}l0 4M${size - pad} ${size - pad}l-4 0M${size - pad} ${size - pad}l0 -4`
            : `M${size - pad} ${size - pad}l-4 0M${size - pad} ${size - pad}l0 -4`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      ) : null}
    </svg>
  );
}

const STAMPS = [
  '⭐', '❤️', '🔥', '✨', '💡', '👍', '😂', '😍', '🤔', '😎', '🎉', '🙌',
  '✅', '❌', '❓', '❗', '📌', '🎯', '🚀', '🌈', '☀️', '🌙', '⚡', '🍀',
  '🐱', '🐶', '🦊', '🐸', '🌸', '🌵', '🍕', '🍩', '☕', '🎵', '🎨', '👾',
];

interface ToolDef {
  id: string;
  label: string;
  icon: JSX.Element;
  hint?: string;
}

const TOOL_DEFS: ToolDef[] = [
  { id: TOOLS.PEN, label: 'Pen', icon: <IconPen />, hint: 'B' },
  { id: TOOLS.MARKER, label: 'Marker', icon: <IconMarker />, hint: 'M' },
  { id: TOOLS.HIGHLIGHTER, label: 'Highlighter', icon: <IconHighlighter />, hint: 'H' },
  { id: TOOLS.NEON, label: 'Neon', icon: <IconNeon />, hint: 'N' },
  { id: TOOLS.ERASER, label: 'Eraser', icon: <IconEraser />, hint: 'E' },
  { id: TOOLS.OBJECT_ERASER, label: 'Cut out', icon: <IconScissors />, hint: 'X' },
  { id: TOOLS.SELECT, label: 'Select', icon: <IconSelect />, hint: 'D' },
  { id: TOOLS.SHAPE, label: 'Shapes', icon: <IconShapes />, hint: 'R' },
  { id: TOOLS.FILL, label: 'Fill', icon: <IconFill />, hint: 'F' },
  { id: TOOLS.TEXT, label: 'Text', icon: <IconText />, hint: 'T' },
  { id: TOOLS.STAMP, label: 'Stamp', icon: <IconStamp />, hint: 'S' },
  { id: TOOLS.LASER, label: 'Laser', icon: <IconLaser />, hint: 'L' },
  { id: TOOLS.PAN, label: 'Move', icon: <IconHand />, hint: 'Space' },
];

/** The tool orb shows the chosen shape itself while the shape tool is active. */
const iconFor = (tools: ToolState) => {
  if (tools.tool === TOOLS.SHAPE) return <ShapePreview shape={tools.shape} size={26} />;
  return TOOL_DEFS.find((t) => t.id === tools.tool)?.icon ?? <IconPen />;
};

/**
 * Right-hand column: the current tool, the brush-size strip that unrolls from
 * its orb, and the colour swatch.
 */
export default function ToolDock({
  tools, onChange, laserEnabled,
}: {
  tools: ToolState;
  onChange: (patch: Partial<ToolState>) => void;
  laserEnabled: boolean;
}) {
  const [panel, setPanel] = useState<'none' | 'tools' | 'sizes' | 'colors' | 'stamps' | 'shapes'>('none');

  const toolRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<HTMLDivElement>(null);
  const colorRef = useRef<HTMLDivElement>(null);

  useDismiss([toolRef, sizeRef, colorRef], () => setPanel('none'), panel !== 'none');

  const toggle = (next: typeof panel) => setPanel((prev) => (prev === next ? 'none' : next));
  const visibleTools = TOOL_DEFS.filter((t) => t.id !== TOOLS.LASER || laserEnabled);

  return (
    <>
      {/* --- tool --- */}
      <div ref={toolRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="orb"
          data-active={panel === 'tools'}
          onClick={() => toggle('tools')}
          aria-label="Choose a tool"
          title="Choose a tool"
        >
          {iconFor(tools)}
        </button>

        {panel === 'tools' ? (
          <div className="popover" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 20 }}>
            <p className="popover__title">Tools</p>
            <div className="tool-grid">
              {visibleTools.map((tool) => (
                <button
                  key={tool.id}
                  type="button"
                  className="tool-cell"
                  data-active={tools.tool === tool.id}
                  onClick={() => {
                    onChange({ tool: tool.id });
                    // Picking a tool that needs a second choice goes straight
                    // to it rather than making you find it.
                    if (tool.id === TOOLS.STAMP) setPanel('stamps');
                    else if (tool.id === TOOLS.SHAPE) setPanel('shapes');
                    else setPanel('none');
                  }}
                  title={tool.hint ? `${tool.label} (${tool.hint})` : tool.label}
                  aria-label={tool.label}
                >
                  {tool.icon}
                  <small>{tool.label}</small>
                </button>
              ))}
            </div>

            <div className="divider" style={{ margin: '14px 0 10px' }} />
            <div className="row row--between">
              <span className="tiny muted">Fill shapes</span>
              <button
                type="button"
                className="toggle"
                data-on={tools.fillShapes}
                onClick={() => onChange({ fillShapes: !tools.fillShapes })}
                aria-label="Fill shapes"
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <div className="tiny muted" style={{ marginBottom: 6 }}>
                Opacity — {Math.round(tools.opacity * 100)}%
              </div>
              <input
                type="range"
                min={5}
                max={100}
                value={Math.round(tools.opacity * 100)}
                onChange={(e) => onChange({ opacity: Number(e.target.value) / 100 })}
                style={{ width: '100%' }}
                aria-label="Opacity"
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* --- size strip --- */}
      <div ref={sizeRef} className="row" style={{ gap: 0, justifyContent: 'flex-end' }}>
        <div className="pill expando" data-open={panel === 'sizes'} style={{ marginRight: 6 }}>
          <div className="sizes">
            {BRUSH_SIZES.map((size: number) => (
              <button
                key={size}
                type="button"
                className="size-dot"
                data-active={tools.size === size}
                onClick={() => onChange({ size })}
                aria-label={`Brush size ${size}`}
              >
                <i style={{ width: dotPx(size), height: dotPx(size) }} />
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="orb"
          data-active={panel === 'sizes'}
          onClick={() => toggle('sizes')}
          aria-label="Brush size"
          title="Brush size"
        >
          <span
            style={{
              width: dotPx(tools.size) + 8,
              height: dotPx(tools.size) + 8,
              borderRadius: '50%',
              background: 'var(--surface-active)',
              boxShadow: 'inset 0 0 0 2px var(--ink)',
            }}
          />
        </button>
      </div>

      {/* --- colour --- */}
      <div ref={colorRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="orb"
          data-active={panel === 'colors'}
          onClick={() => toggle('colors')}
          aria-label="Colour"
          title="Colour"
        >
          <span className="orb__swatch" style={{ background: tools.color }} />
        </button>

        {panel === 'colors' ? (
          <div className="popover" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 20 }}>
            <p className="popover__title">Colour</p>
            <div className="swatches">
              {PALETTE.map((color: string) => (
                <button
                  key={color}
                  type="button"
                  className="swatch"
                  style={{ background: color }}
                  data-active={tools.color.toLowerCase() === color.toLowerCase()}
                  onClick={() => onChange({ color })}
                  aria-label={color}
                />
              ))}
            </div>
            <div className="divider" style={{ margin: '14px 0 10px' }} />
            <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
              <input
                type="color"
                value={tools.color}
                onChange={(e) => onChange({ color: e.target.value })}
                style={{
                  width: 44, height: 44, border: 0, background: 'none', padding: 0, borderRadius: '50%',
                }}
                aria-label="Custom colour"
              />
              <span className="tiny muted">Pick any colour</span>
            </label>
          </div>
        ) : null}

        {panel === 'shapes' ? (
          <div className="popover popover--stack" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 20 }}>
            <p className="popover__title">Shapes</p>
            <div className="shape-groups">
              {SHAPE_GROUPS.map((group: { id: string; label: string; shapes: string[] }) => (
                <div key={group.id}>
                  <p className="shape-groups__label">{group.label}</p>
                  <div className="shapes">
                    {group.shapes.map((shape) => (
                      <button
                        key={shape}
                        type="button"
                        className="shape-cell"
                        data-active={tools.tool === TOOLS.SHAPE && tools.shape === shape}
                        onClick={() => {
                          onChange({ tool: TOOLS.SHAPE, shape });
                          setPanel('none');
                        }}
                        title={SHAPE_LABELS[shape as keyof typeof SHAPE_LABELS] ?? shape}
                        aria-label={SHAPE_LABELS[shape as keyof typeof SHAPE_LABELS] ?? shape}
                      >
                        <ShapePreview shape={shape} size={28} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="divider" style={{ margin: '12px 0 10px' }} />
            <div className="row row--between">
              <span className="tiny muted">
                Fill {isOpenShape(tools.shape) ? '(not for lines)' : ''}
              </span>
              <button
                type="button"
                className="toggle"
                data-on={tools.fillShapes}
                disabled={isOpenShape(tools.shape)}
                onClick={() => onChange({ fillShapes: !tools.fillShapes })}
                aria-label="Fill shapes"
              />
            </div>
            <p className="tiny muted" style={{ margin: '10px 0 0' }}>
              Hold <kbd>Shift</kbd> while dragging to keep it square.
            </p>
          </div>
        ) : null}

        {panel === 'stamps' ? (
          <div className="popover" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 20 }}>
            <p className="popover__title">Stamps</p>
            <div className="stamps">
              {STAMPS.map((char) => (
                <button
                  key={char}
                  type="button"
                  className="stamp"
                  data-active={tools.stamp === char}
                  onClick={() => { onChange({ stamp: char, tool: TOOLS.STAMP }); setPanel('none'); }}
                  aria-label={`Stamp ${char}`}
                >
                  {char}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/** Map a world-space brush size onto a readable dot in the size strip. */
function dotPx(size: number) {
  return Math.round(Math.min(30, 3 + size * 0.62));
}
